import { ComponentProps, createElement, useEffect, useRef } from 'react';
import styled from 'styled-components';

export const GraphWrapperStyled = styled.div<{
  $height?: string;
  $heightSM?: string;
}>`
  height: ${({ $height }) => $height || '300px'};
  width: 100%;
  position: relative;
  padding-right: 12px;
  padding-bottom: 12px;

  @media (max-width: 800px) {
    height: ${({ $heightSM }) => $heightSM || '300px'};
  }
`;

/**
 * GraphWrapper — chart container that also strips recharts'
 * auto-injected tabindex attributes from its SVG/group descendants.
 *
 * recharts puts `tabindex="0"` on `<svg>`, `g.recharts-layer.recharts-pie`,
 * and individual chart sectors so keyboard users can step through data
 * points. Chrome paints a focus ring on those SVG elements that ignores
 * standard CSS `outline: none` overrides (SVG focus rings on Chrome
 * are sometimes drawn as a non-outline UA decoration that's stubborn
 * to suppress with CSS alone).
 *
 * This wrapper removes those tabindex attributes after mount AND
 * watches the subtree with a MutationObserver so re-renders (recharts
 * re-injects them when data updates) don't bring them back. The visual
 * chart and tooltips still work for mouse users; keyboard users still
 * get the surrounding numeric legend (which is regular HTML text).
 */
export const GraphWrapper = (
  props: ComponentProps<typeof GraphWrapperStyled>
) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;

    const strip = () => {
      root
        .querySelectorAll<HTMLElement | SVGElement>('[tabindex]')
        .forEach((el) => el.removeAttribute('tabindex'));
    };

    strip();

    // recharts re-creates SVG nodes on data updates / resize; observe
    // descendants so we strip any newly-added tabindex too.
    const observer = new MutationObserver(strip);
    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['tabindex'],
    });

    return () => observer.disconnect();
  }, []);

  // File is `.ts` (not `.tsx`) so use createElement instead of JSX.
  return createElement(GraphWrapperStyled, { ref, ...props });
};

export const AreaTooltip = styled.div`
  width: max-content;
  height: max-content;
  padding: 10px;
  background-color: white;
  border-radius: 10px;
  position: relative;
  box-shadow: 0px 20px 30px rgba(0, 0, 0, 0.1);
`;

export const GridCharts = styled.div<{
  $width?: string;
  $widthMD?: string;
  $m?: string;
  $mSM?: string;
  $p?: string;
  $pSM?: string;
  $gap?: string;
  $gapSM?: string;
}>`
  display: flex;
  gap: ${({ $gap }) => $gap || '24px'};
  margin-bottom: 40px;
  overflow-x: auto;
  padding: ${({ $p }) => $p || '10px 50px 40px 50px'};
  margin: ${({ $m }) => $m || '0 -50px 0 -50px'};
  scroll-snap-type: x mandatory;

  > div {
    min-width: ${({ $width }) => $width || '45%'};

    @media (max-width: 800px) {
      scroll-snap-align: center;
    }
  }

  @media (max-width: 1100px) {
    > div {
      min-width: ${({ $widthMD }) => $widthMD || '70%'};
    }
  }

  @media (max-width: 800px) {
    display: flex;
    gap: ${({ $gapSM }) => $gapSM || '20px'};
    overflow-x: auto;
    padding: ${({ $pSM }) => $pSM || '10px 30px 40px 30px'};
    margin: ${({ $mSM }) => $mSM || '0 -30px 0 -30px'};

    > div {
      min-width: 80vw;
    }
  }

  ::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;
